import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import ApiState from "@/components/base/ApiState";
import BusinessGrid from "@/components/feature/BusinessGrid";
import CategoryCard from "@/components/feature/CategoryCard";
import { getBusinesses, getCategories, getCategoryBySlug, getLocations } from "@/services/publicApi";
import { useApiResource } from "@/hooks/useApiResource";
import { parseCategoryPlaceSlug, categoryPath } from "@/utils/slug";

const PAGE_SIZE = 9;

export default function CategoryDetail() {
  const { slug = "" } = useParams();
  // The slug may be a plain category slug (`restaurants`) or a location-scoped
  // SEO slug (`restaurants-in-mysore`). Parse it before resolving the category.
  const { categorySlug, placeSlug } = parseCategoryPlaceSlug(slug);
  const { data, loading, error, reload } = useApiResource(
    () => Promise.all([getCategoryBySlug(categorySlug), getCategories(), getLocations(), getBusinesses()]),
    [slug],
  );
  const category = data?.[0] ?? null;
  const categories = data?.[1] ?? [];
  const locations = data?.[2] ?? [];
  const businesses = data?.[3] ?? [];

  const [locationFilter, setLocationFilter] = useState("");
  const [sort, setSort] = useState("relevance");
  const [minRating, setMinRating] = useState(0);
  const [openNow, setOpenNow] = useState(false);
  const [page, setPage] = useState(1);

  const placedLocation = useMemo(
    () => (placeSlug ? locations.find((l) => l.slug === placeSlug) || null : null),
    [locations, placeSlug],
  );

  useEffect(() => {
    setLocationFilter(placedLocation?.id ?? "");
    setMinRating(0);
    setOpenNow(false);
    setPage(1);
  }, [slug, placedLocation?.id]);

  const categoryBusinesses = useMemo(
    () => businesses.filter((b) => b.categoryId === category?.id),
    [businesses, category],
  );

  const listedLocations = useMemo(() => {
    const ids = new Set(categoryBusinesses.map((b) => b.locationId));
    return locations.filter((l) => ids.has(l.id));
  }, [categoryBusinesses, locations]);

  const filtered = useMemo(() => {
    let list = categoryBusinesses;
    if (locationFilter) list = list.filter((b) => b.locationId === locationFilter);
    if (minRating > 0) list = list.filter((b) => b.rating >= minRating);
    if (openNow) list = list.filter((b) => b.openNow);
    if (sort === "rating") list = [...list].sort((a, b) => b.rating - a.rating);
    else if (sort === "reviews") list = [...list].sort((a, b) => b.reviewCount - a.reviewCount);
    else if (sort === "name") list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    else list = [...list].sort((a, b) => Number(b.isFeatured) - Number(a.isFeatured) || b.rating - a.rating);
    return list;
  }, [categoryBusinesses, locationFilter, sort, minRating, openNow]);

  const activeFilterCount =
    (locationFilter ? 1 : 0) + (minRating > 0 ? 1 : 0) + (openNow ? 1 : 0);

  const clearFilters = () => {
    setLocationFilter("");
    setMinRating(0);
    setOpenNow(false);
    setPage(1);
  };

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const relatedCategories = categories.filter((c) => c.id !== category?.id).slice(0, 8);

  if (!loading && !error && !category) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20">
        <EmptyState
          icon="ri-price-tag-3-line"
          title="Category not found"
          description="This category doesn't exist or may have been renamed."
          action={{ label: "All categories", to: "/categories" }}
        />
      </div>
    );
  }

  return (
    <>
      <Seo
        title={category ? `${category.name}${placedLocation ? ` in ${placedLocation.name}` : " in Mysore Region"}` : "Category"}
        description={category ? `${category.description}. Browse verified ${category.name.toLowerCase()}${placedLocation ? ` in ${placedLocation.name}` : " across Mysore, Mandya, Chamarajanagar and Kodagu"}.` : undefined}
        keywords={category ? `${category.name}${placedLocation ? ` ${placedLocation.name}` : " Mysore"}, ${category.name} near me, local ${category.name}` : undefined}
        path={categoryPath(category?.slug ?? categorySlug, placedLocation?.slug)}
      />

      <div className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-8 md:py-10">
          <Breadcrumbs
            items={[
              { label: "Categories", to: "/categories" },
              { label: category?.name || "…", to: placedLocation ? categoryPath(categorySlug) : undefined },
              ...(placedLocation ? [{ label: placedLocation.name }] : []),
            ]}
          />
          <div className="mt-4 flex items-start gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-foreground-950">
              <i className={`${category?.icon || "ri-price-tag-3-line"} text-2xl`}></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl md:text-4xl font-extrabold tracking-tight text-foreground-950">
                {category?.name || "Loading…"}
                {placedLocation ? ` in ${placedLocation.name}` : ""}
              </h1>
              <p className="mt-1.5 max-w-2xl text-sm md:text-base text-foreground-600">
                {category?.description}
              </p>
              <p className="mt-1 text-xs font-semibold text-foreground-500">
                {loading ? "" : `${categoryBusinesses.length} listed in this category`}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 lg:gap-8">
          <div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={clearFilters}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold cursor-pointer ${
                    activeFilterCount === 0 ? "bg-primary-500 text-foreground-950" : "border border-background-300 text-foreground-700 hover:border-primary-400"
                  }`}
                >
                  All listings
                </button>
                {listedLocations.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => {
                      setLocationFilter(l.id);
                      setPage(1);
                    }}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold cursor-pointer ${
                      locationFilter === l.id ? "bg-primary-500 text-foreground-950" : "border border-background-300 text-foreground-700 hover:border-primary-400"
                    }`}
                  >
                    {l.name}
                  </button>
                ))}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-background-100 pt-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Rating</span>
                {[0, 4, 4.5].map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      setMinRating(r);
                      setPage(1);
                    }}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold cursor-pointer ${
                      minRating === r ? "bg-secondary-500 text-background-50" : "border border-background-300 text-foreground-700 hover:border-secondary-400"
                    }`}
                  >
                    {r === 0 ? "Any" : `${r}+`}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setOpenNow((v) => !v);
                    setPage(1);
                  }}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold cursor-pointer ${
                    openNow ? "bg-accent-500 text-background-50" : "border border-background-300 text-foreground-700 hover:border-accent-400"
                  }`}
                >
                  <i className="ri-time-line"></i> Open now
                </button>
                <div className="ml-auto flex items-center gap-2">
                  <label htmlFor="cat-sort" className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    Sort
                  </label>
                  <select
                    id="cat-sort"
                    value={sort}
                    onChange={(e) => {
                      setSort(e.target.value);
                      setPage(1);
                    }}
                    className="rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm focus:border-primary-400 focus:outline-none cursor-pointer"
                  >
                    <option value="relevance">Best match</option>
                    <option value="rating">Highest rated</option>
                    <option value="reviews">Most reviewed</option>
                    <option value="name">Name (A–Z)</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="mt-5">
              <ApiState
                loading={loading}
                error={error}
                onRetry={reload}
                skeleton={
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="h-80 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
                    ))}
                  </div>
                }
                isEmpty={filtered.length === 0}
                empty={
                  <EmptyState
                    icon="ri-store-3-line"
                    title="No listings yet in this category"
                    description="We're still onboarding businesses here. Be the first to list and get discovered."
                    action={{ label: "List your business", to: "/list-your-business" }}
                  />
                }
              >
                <BusinessGrid businesses={paged} columns={2} />
                <Pagination page={page} totalPages={totalPages} onChange={setPage} />
              </ApiState>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-100/60 p-5">
              <h2 className="font-heading text-sm font-bold text-foreground-950">Looking for more?</h2>
              <p className="mt-1.5 text-sm text-foreground-600">
                Can't find what you need in {category?.name || "this category"}? Search the full directory.
              </p>
              <Link
                to="/directory"
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-line"></i> Search directory
              </Link>
            </div>
            <div>
              <h2 className="font-heading text-sm font-bold text-foreground-950">Related categories</h2>
              <div className="mt-3 space-y-2">
                {relatedCategories.map((c) => (
                  <CategoryCard key={c.id} category={c} placeSlug={placedLocation?.slug} />
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}